import { useQuery } from '@tanstack/react-query';

import type { GetShipBuyerPacketToSellerResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get ship buyer packet to seller data from a purchased vehicle
 */
export const getShipBuyerPacketToSeller = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetShipBuyerPacketToSellerResponse> => {
	const response = await apiClient.get<GetShipBuyerPacketToSellerResponse>(
		ordersEndpoints.shipBuyerPacketToSeller(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting ship buyer packet to seller data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetShipBuyerPacketToSeller = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.shipBuyerPacketToSeller.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get ship buyer packet to seller data without an order ID.');
			}
			return getShipBuyerPacketToSeller(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};

